import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Box, InputAdornment, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import VisibilityActionButton from '../../../components/designe/ActionButtons/VisibilityActionButton';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useFeature } from '../../../hooks/use-feature';
import { AppFeatureKey } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import {
  AuthPrimaryButton,
  AuthSecondaryButton,
} from '../components/auth-button';
import AuthLanguageSelector from '../components/auth-language-selector/AuthLanguageSelector';
import AuthTextField from '../components/auth-text-field';
import { useStyles } from './SignIn.styles';
import { useSignInPage } from './useSignInPage';

const SignIn = () => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { classes } = useStyles(theme);
  const { navigate } = useNavigationStack();
  const {
    [AppFeatureKey.appleAuthorization]: appleAuthorizationActive,
    [AppFeatureKey.facebookAuthorization]: facebookAuthorizationActive,
  } = useFeature();
  const { formik, signInWithGoogle, signInWithApple, signInWithFacebook } =
    useSignInPage(t, () => navigate(Paths.root, { replace: true }));
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    formik.handleSubmit(event);
  };

  return (
    <div className={classes.signInWrapper}>
      {isMobile ? null : (
        <AuthLanguageSelector className={classes.desktopLanguageSelector} />
      )}
      <div className={classes.signInContentWrapper}>
        {isMobile && (
          <AuthLanguageSelector className={classes.mobileLanguageSelector} />
        )}
        <form
          onSubmit={handleSubmit}
          action={Paths.signIn}
          method="post"
          className={classes.signInForm}>
          <AuthTextField
            placeholder={t('common.email')}
            fullWidth
            name="email"
            formik={formik}
            className={classes.textField}
          />
          <AuthTextField
            placeholder={t('signIn.password')}
            fullWidth
            name="password"
            type={showPassword ? 'text' : 'password'}
            formik={formik}
            className={classes.textField}
            errorIconRightPadding={40}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <VisibilityActionButton
                    isActive={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputAdornment>
              ),
            }}
          />

          <div className={classes.signInForgotPasswordButton}>
            <Link to={Paths.forgotPassword}> {t('signIn.forgotPassword')}</Link>
          </div>

          <AuthPrimaryButton type="submit" fullWidth>
            {t('signIn.signIn')}
          </AuthPrimaryButton>
        </form>

        <div className={classes.signInDivider}>
          <div />
          <span>{t('signIn.or')}</span>
          <div />
        </div>

        <Box display="flex" gap={2} flexDirection="column" width="100%">
          <AuthSecondaryButton
            className={classes.signInButtonWithIcon}
            fullWidth
            onClick={() => signInWithGoogle()}>
            <GoogleIcon />
            {t('signIn.signInWithGoogle')}
          </AuthSecondaryButton>
          {facebookAuthorizationActive && (
            <AuthSecondaryButton
              className={classes.signInButtonWithIcon}
              fullWidth
              onClick={() => signInWithFacebook()}>
              <FacebookIcon />
              {t('signIn.signInWithFacebook')}
            </AuthSecondaryButton>
          )}

          {appleAuthorizationActive && (
            <AuthSecondaryButton
              className={classes.signInButtonWithIcon}
              fullWidth
              onClick={() => signInWithApple()}>
              <AppleIcon />
              {t('signIn.signInWithApple')}
            </AuthSecondaryButton>
          )}
        </Box>
        <div className={classes.signInModeSwitcher}>
          {t('signIn.dontHaveAccount')}
          <Link className={classes.signInModeSwitcherButton} to={Paths.signUp}>
            {t('signIn.signUp')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignIn;

const GoogleIcon = () => {
  return (
    <svg viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"></path>
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"></path>
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"></path>
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"></path>
    </svg>
  );
};

const AppleIcon = () => {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"></path>
    </svg>
  );
};

const FacebookIcon = () => {
  return (
    <svg viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"></path>
    </svg>
  );
};
